import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllFirmwareDetails, deleteFirmware, downloadFirmware } from "../commonApi";
import AddFirmwareDialog from "./AddFirmwareDialog";
import { DownloadButton } from "../components/DownloadButton";
import { DeleteButton } from "../components/DeleteButton";
import { DeleteModal } from "../components/DeleteModal";
import "../assets/css/Action.css";
import { downloadFile } from "../General/DownloadFile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Loader } from "../Loader";

function FirmwareDetailsGrid() {
  const navigate = useNavigate();
  const getInitialPage = () => {
    const savedPage = localStorage.getItem("pagination-page-firmware-details");
    return savedPage ? parseInt(savedPage, 10) - 1 : 0; // 0-based
  };
  const [page, setPage] = useState(getInitialPage());
  const getInitialPerPage = () => {
    const savedPerPage = localStorage.getItem("pagination-perPage-firmware-details");
    return savedPerPage ? parseInt(savedPerPage, 10) : 10;
  };
  const [countPerPage, setCountPerPage] = useState(getInitialPerPage());
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortColumn, setSortColumn] = useState("id");
  const [searchTerm, setSearchTerm] = useState("");
  const [firmwareData, setFirmwareData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isAddFirmwareOpen, setAddFirmwareOpen] = useState(false);
  const [deleteFirmwareId, setDeleteFirmwareId] = useState(null);
  const [deleteFirmwareName, setDeleteFirmwareName] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const resetTriggerRef = useRef(0);

  useEffect(() => {
    let isCancelled = false;
    const fetchData = async () => {
      if (!isCancelled) {
        await fetchFirmwareDetails();
      }
    };
    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [page, countPerPage, sortColumn, sortOrder, searchTerm]);

  const fetchFirmwareDetails = async () => {
    setLoading(true);
    try {
      const params = {
        pageNo: page,
        pageSize: countPerPage,
        sortOrder: sortOrder,
        sortBy: sortColumn,
        searchValue: searchTerm,
      };

      const response = await getAllFirmwareDetails(params);
      const data = (response?.data?.content || []).filter(
        (item) => item && typeof item === "object" && item.id
      );
      setFirmwareData(data);
      setTotalRows(response?.data?.totalElements ?? 0);
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Error fetching firmware details!");
      setFirmwareData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
    localStorage.setItem("pagination-page-firmware-details", "1");
    resetTriggerRef.current += 1;
  };

  const handlePerRowsChange = (newPerPage, newPage) => {
    setCountPerPage(newPerPage);
    setPage(newPage - 1);
    localStorage.setItem("pagination-perPage-firmware-details", newPerPage.toString());
    localStorage.setItem("pagination-page-firmware-details", newPage.toString());
    resetTriggerRef.current += 1;
  };

  const handleSort = (column, sortDirection) => {
    setSortColumn(column.selector || "id");
    setSortOrder(sortDirection);
    setPage(0);
    localStorage.setItem("pagination-page-firmware-details", "1");
    resetTriggerRef.current += 1;
  };

  const handlePageChange = (newPage) => {
    const zeroBasedPage = newPage - 1;
    if (zeroBasedPage === page || zeroBasedPage < 0) return;
    setPage(zeroBasedPage);
    localStorage.setItem("pagination-page-firmware-details", newPage.toString());
    resetTriggerRef.current += 1;
  };

  const handleDelete = (id, name) => {
    setDeleteFirmwareId(id);
    setDeleteFirmwareName(name);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (isDeleteDisabled) return;
    setIsDeleteDisabled(true);
    try {
      await deleteFirmware(deleteFirmwareId);
      toast.success(`${deleteFirmwareName} deleted successfully!`);
      setPage(0);
      localStorage.setItem("pagination-page-firmware-details", "1");
      resetTriggerRef.current += 1;
      await fetchFirmwareDetails();
    } catch (error) {
      toast.error(error.response?.data?.error?.details?.errorMessage || error.response?.data?.message || "Failed to delete firmware!");
    } finally {
      setIsDeleteDisabled(false);
      setDeleteModalOpen(false);
    }
  };

  const handleDownload = async (downloadFirmwareId) => {
    try {
      const response = await downloadFirmware(downloadFirmwareId);
      if (response && response.data) {
        const downloadedFileName = downloadFile(response);
        toast.success(`${downloadedFileName} Downloaded successfully`);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to download firmware!");
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row?.fileName || <span style={{ position: "absolute", left: "58px", top: "18px" }}>—</span>,
      sortable: true,
      width: "400px",
    },
    {
      name: "Version",
      selector: (row) => row?.version || <span style={{ position: "absolute", left: "58px", top: "18px" }}>—</span>,
      sortable: true,
      width: "300px",
    },
    {
      name: "Model",
      selector: (row) => row?.deviceModelName || <span style={{ position: "absolute", left: "58px", top: "18px" }}>—</span>,
      sortable: true,
      width: "350px",
    },
    {
      name: "Download",
      cell: (row) => (
        <button className="btn action-icon" onClick={() => handleDownload(row.id)}>
          <DownloadButton />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <button className="btn action-icon" onClick={() => handleDelete(row.id, row.fileName)}>
          <DeleteButton />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="custom_tab_wrapper">
      <AddFirmwareDialog
        isOpen={isAddFirmwareOpen}
        onClose={() => setAddFirmwareOpen(false)}
        refreshData={fetchFirmwareDetails}
      />
      <ul className="nav nav-tabs" id="myTab" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className="nav-link active"
            id="firmware-tab"
            data-bs-toggle="tab"
            data-bs-target="#firmware"
            type="button"
            role="tab"
            aria-controls="firmware"
            aria-selected="true"
          >
            Firmwares: {totalRows}
          </button>
        </li>
      </ul>
      <div className="d-flex justify-content-between mb-2 custom_table_action">
        <input
          className="form-control w-25"
          type="text"
          placeholder="Search by Name"
          value={searchTerm}
          onChange={handleSearchInputChange}
        />
        <button
          className="btn primary_btn"
          onClick={() => setAddFirmwareOpen(true)}
          data-bs-toggle="tooltip"
          data-bs-placement="left"
          title="Add New Firmware"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>
      <div className="table-responsive h-100vh-150px">
        {loading ? (
          <div className="spin-loader">
            <Loader />
          </div>
        ) : (
          <div className="custom_table">
            <DataTable
              key={`datatable-firmware-details-${page + 1}-${countPerPage}-${resetTriggerRef.current}`}
              columns={columns}
              data={firmwareData}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              progressPending={loading}
              paginationDefaultPage={page + 1}
              paginationPerPage={countPerPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              onSort={handleSort}
              sortServer
              highlightOnHover
              fixedHeader
              fixedHeaderScrollHeight="440px"
              paginationComponentOptions={{
                rowsPerPageText: "Rows per page:",
                rangeSeparatorText: "of",
                noRowsPerPage: false,
                selectAllRowsItem: false,
              }}
              paginationResetDefaultPage={resetTriggerRef.current > 0}
            />
          </div>
        )}
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirmDelete={confirmDelete}
        isDeleteDisabled={isDeleteDisabled}
      />
    </div>
  );
}

export default FirmwareDetailsGrid;